import type { TableRow } from '@/components/ui/table/table.type';
import { getByPath } from '@/utils';

export type ToggleRowSelectionOptions = {
  shiftKey?: boolean;
  ctrlKey?: boolean;
  metaKey?: boolean;
  rowIndex: number;
};

type SelectionState = {
  selectedData: string[]; // ex: ['id3','id7']
  dataToDisplay: TableRow[]; // visible rows
  lastSelectedRowIndex: number; // -1 if none
};

type ToggleResult = Pick<
  SelectionState,
  'selectedData' | 'lastSelectedRowIndex'
>;

type SelectAction = 'range-add' | 'range-remove' | 'unit-add' | 'unit-remove';

function findRowIndex(
  selector: string,
  rows: TableRow[],
  selectorPath: string,
  givenIndex?: number
): number {
  if (Number.isInteger(givenIndex)) return givenIndex as number;
  return rows.findIndex(
    row => row && getByPath(row, selectorPath) === selector
  );
}

function selectorsInRange(
  rows: TableRow[],
  selectorPath: string,
  start: number,
  end: number
): string[] {
  const selectedRowIds: string[] = [];
  for (let i = Math.min(start, end); i <= Math.max(start, end); i += 1) {
    const row = rows[i];
    if (!row) continue;
    const selectedRowId = getByPath(row, selectorPath);
    if (selectedRowId !== undefined) selectedRowIds.push(selectedRowId);
  }
  return selectedRowIds;
}

function addMany(base: string[], toAdd: string[]): string[] {
  if (toAdd.length === 0) return base;
  const s = new Set(base);
  for (const x of toAdd) s.add(x);
  return Array.from(s);
}

function removeMany(base: string[], toRemove: string[]): string[] {
  if (toRemove.length === 0) return base;
  const remove = new Set(toRemove);
  return base.filter(x => !remove.has(x));
}

function computeAction(
  isSelected: boolean,
  opts: ToggleRowSelectionOptions,
  canUseRange: boolean
): SelectAction {
  const { shiftKey, ctrlKey, metaKey } = opts;

  const hasRangeModifier = !!(shiftKey || ctrlKey || metaKey);
  if (hasRangeModifier && canUseRange) {
    return isSelected ? 'range-remove' : 'range-add';
  }

  return isSelected ? 'unit-remove' : 'unit-add';
}

export function computeNextSelection<
  TState extends SelectionState,
  TConfig extends {
    dataSelectorPath?: string; // ex: 'rowId' or 'meta.id'
  }
>(
  rowSelector: string,
  options: ToggleRowSelectionOptions,
  state: TState,
  config: TConfig
): ToggleResult {
  const selectorPath = config.dataSelectorPath || 'rowId';
  const { selectedData, dataToDisplay, lastSelectedRowIndex } = state;

  const currentIndex = findRowIndex(
    rowSelector,
    dataToDisplay,
    selectorPath,
    options.rowIndex
  );
  const isRowIndexValid = Number.isInteger(currentIndex) && currentIndex >= 0;
  const isLastIndexValid =
    Number.isInteger(lastSelectedRowIndex) && lastSelectedRowIndex >= 0;

  const isSelected = selectedData.includes(rowSelector);
  const canUseRange = isRowIndexValid && isLastIndexValid;

  const action = computeAction(isSelected, options, canUseRange);

  let nextSelectedRowIds: string[] = selectedData;

  switch (action) {
    case 'range-add': {
      const inRange = selectorsInRange(
        dataToDisplay,
        selectorPath,
        currentIndex,
        lastSelectedRowIndex
      );
      nextSelectedRowIds = addMany(selectedData, inRange);
      break;
    }
    case 'range-remove': {
      const inRange = selectorsInRange(
        dataToDisplay,
        selectorPath,
        currentIndex,
        lastSelectedRowIndex
      );
      nextSelectedRowIds = removeMany(selectedData, inRange);
      break;
    }
    case 'unit-remove': {
      nextSelectedRowIds = selectedData.filter(s => s !== rowSelector);
      break;
    }
    case 'unit-add': {
      nextSelectedRowIds = isSelected
        ? selectedData
        : [...selectedData, rowSelector];
      break;
    }
    default:
      break;
  }

  const nextLastIndex = isRowIndexValid ? currentIndex : lastSelectedRowIndex;

  return {
    selectedData: nextSelectedRowIds,
    lastSelectedRowIndex: nextLastIndex
  };
}
